import React, { useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import baseUrl from "../api/utils";

// Usage:
//   Parties page:   <BulkAddParty partyType="SUPPLIER" />
//   Customer page:  <BulkAddParty partyType="CUSTOMER" />
const BulkAddParty = ({ partyType }) => {
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);
  const token = sessionStorage.getItem("token");
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null); // BulkPartyUploadResultDto from backend

  const partyLabel = partyType === "SUPPLIER" ? "Supplier" : "Customer";

  // ── Download Template (from backend, not static asset) ──
  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      const response = await axios.get(`${baseUrl}/admin/bulkPartyTemplate`, {
        headers: { Authorization: token },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "Party_Upload_Template.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      MySwal.fire({
        icon: "error",
        title: "Download Failed",
        text: "Could not download the template. Please try again.",
      });
    } finally {
      setDownloading(false);
    }
  };

  // ── File select ──
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".xlsx")) {
      MySwal.fire({
        icon: "error",
        title: "Invalid File",
        text: "Please upload a valid .xlsx file.",
      });
      return;
    }

    setSelectedFile(file);
    setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (!file.name.endsWith(".xlsx")) {
      MySwal.fire({
        icon: "error",
        title: "Invalid File",
        text: "Please upload a valid .xlsx file.",
      });
      return;
    }
    setSelectedFile(file);
    setResult(null);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Import ──
  const handleImport = async () => {
    if (!selectedFile) {
      MySwal.fire({
        icon: "warning",
        title: "No File Selected",
        text: "Please choose an Excel file to upload first.",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("partyType", partyType);

    try {
      setUploading(true);
      setResult(null);

      const response = await axios.post(`${baseUrl}/admin/bulkAddParty`, formData, {
        headers: {
          Authorization: token,
          "Content-Type": "multipart/form-data",
        },
      });

      setResult(response.data);

      MySwal.fire({
        title: `${partyLabel}s Imported`,
        text: `${response.data.savedCount} of ${response.data.totalRows} ${partyLabel.toLowerCase()}(s) added successfully.`,
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate("/unauthorized");
        return;
      }
      if (error.response && error.response.status === 403) {
        MySwal.fire({
          icon: "error",
          title: "Access Denied",
          text: "Only ADMIN or CASHIER can bulk add.",
        });
        return;
      }
      MySwal.fire({
        icon: "error",
        title: "Upload Failed",
        text: "Something went wrong while uploading. Please try again.",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="invoice-card-wrapper vt-card-wrapper">
        <div>
          <div className="card invoice-card">
            <div className="card-body p-0">

              {/* ══ HEADER ══ */}
              <div className="invoice-right-panel-header">
                <div className="vt-right-header-left">
                  <div onClick={() => navigate(-1)} style={{ cursor: "pointer", fontSize: "16px", color: "#333" }}>
                    <i className="fa-solid fa-arrow-left"></i>
                  </div>
                  <h6 className="invoice-right-panel-title">Bulk Add {partyLabel}s</h6>
                </div>
                <div onClick={() => navigate(-1)} style={{ cursor: "pointer", fontSize: "18px", color: "#555" }}>
                  <i className="fa-solid fa-xmark"></i>
                </div>
              </div>

              {/* ══ BODY ══ */}
              <div style={{ padding: "16px 20px" }}>
                <p className="text-muted" style={{ marginBottom: 20, fontSize: 13 }}>
                  Download the template, fill in your {partyLabel.toLowerCase()} details, then upload
                  the file to add multiple {partyLabel.toLowerCase()}s at once.
                </p>

                {/* ── Step 1: Download Template ── */}
                <div style={{
                  background: "#f0f7ff",
                  borderLeft: "3px solid #1a73e8",
                  borderRadius: 8,
                  padding: "14px 16px",
                  marginBottom: 16,
                }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1" style={{ fontSize: 14 }}>Step 1 — Download Template</h6>
                      <span className="text-muted" style={{ fontSize: "0.8rem" }}>
                        Name, Phone, Email are mandatory columns
                      </span>
                    </div>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={handleDownloadTemplate}
                      disabled={downloading}
                    >
                      <i className="fa-solid fa-download mr-1"></i>
                      {downloading ? "..." : "Download"}
                    </button>
                  </div>
                </div>

                {/* ── Step 2: Upload ── */}
                <div style={{
                  background: "#f5f5f5",
                  borderLeft: "3px solid #aaa",
                  borderRadius: 8,
                  padding: "14px 16px",
                  marginBottom: 16,
                }}>
                  <h6 className="mb-1" style={{ fontSize: 14 }}>Step 2 — Upload Filled Excel</h6>
                  <span className="text-muted" style={{ fontSize: "0.8rem" }}>
                    Only .xlsx files are accepted
                  </span>

                  <div style={{ marginTop: 14 }}>
                    {!selectedFile ? (
                      <div
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                          border: "2px dashed #ccc",
                          borderRadius: 6,
                          padding: "28px 16px",
                          textAlign: "center",
                          cursor: "pointer",
                          background: "#fff",
                        }}
                      >
                        <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: 26, color: "#aaa" }}></i>
                        <div style={{ marginTop: 8, fontSize: "0.85rem" }}>
                          Click to browse or drag & drop your Excel file here
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".xlsx"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                      </div>
                    ) : (
                      <div
                        className="d-flex justify-content-between align-items-center"
                        style={{
                          padding: "10px 14px",
                          borderRadius: 6,
                          background: "#fff",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <div style={{ fontSize: 13 }}>
                          <i className="fa-solid fa-file-excel mr-2" style={{ color: "#27ae60" }}></i>
                          {selectedFile.name}
                        </div>
                        <button
                          className="hidebtn icontrash"
                          onClick={handleRemoveFile}
                          title="Remove file"
                        >
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                    )}

                    <button
                      className="btn btn-danger btn-sm"
                      style={{ marginTop: 14 }}
                      disabled={!selectedFile || uploading}
                      onClick={handleImport}
                    >
                      {uploading ? (
                        <>Importing...</>
                      ) : (
                        <>
                          <i className="fa-solid fa-check mr-1"></i>
                          Import {partyLabel}s
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* ── Result Summary ── */}
                {result && (
                  <div style={{
                    background: "#f0fdf4",
                    borderLeft: "3px solid #27ae60",
                    borderRadius: 8,
                    padding: "14px 16px",
                    marginBottom: 16,
                  }}>
                    <div style={{ color: "#137333", fontWeight: 600, marginBottom: 6, fontSize: 13 }}>
                      <i className="fa-solid fa-circle-check mr-2"></i>
                      Import Complete
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "#333" }}>
                      {result.savedCount} of {result.totalRows} {partyLabel.toLowerCase()}(s) added.
                      {result.duplicates?.length > 0 && (
                        <> {result.duplicates.length} duplicate(s) skipped.</>
                      )}
                      {result.invalidRows?.length > 0 && (
                        <> {result.invalidRows.length} row(s) skipped (missing mandatory fields).</>
                      )}
                    </div>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      style={{ marginTop: 12 }}
                      onClick={() => navigate(-1)}
                    >
                      View {partyLabel}s
                    </button>
                  </div>
                )}

                {/* ── Duplicates List ── */}
                {result && result.duplicates?.length > 0 && (
                  <div style={{
                    background: "#fff8e1",
                    borderLeft: "3px solid #f0ad4e",
                    borderRadius: 8,
                    padding: "14px 16px",
                    marginBottom: 16,
                  }}>
                    <div style={{ color: "#8a6d1a", fontWeight: 600, marginBottom: 10, fontSize: 13 }}>
                      <i className="fa-solid fa-triangle-exclamation mr-2"></i>
                      Duplicates Skipped (same name + phone already exists)
                    </div>
                    <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                      {result.duplicates.map((row, idx) => (
                        <li key={idx} style={{ fontSize: "0.85rem", marginBottom: 4 }}>
                          Row {row.rowNumber}: {row.name} ({row.phone})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* ── Invalid Rows List ── */}
                {result && result.invalidRows?.length > 0 && (
                  <div style={{
                    background: "#fff0f0",
                    borderLeft: "3px solid #e74c3c",
                    borderRadius: 8,
                    padding: "14px 16px",
                  }}>
                    <div style={{ color: "#d93025", fontWeight: 600, marginBottom: 10, fontSize: 13 }}>
                      <i className="fa-solid fa-circle-exclamation mr-2"></i>
                      Rows Skipped — Missing Name, Phone, or Email
                    </div>
                    <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                      {result.invalidRows.map((row, idx) => (
                        <li key={idx} className="text-danger" style={{ fontSize: "0.85rem", marginBottom: 4 }}>
                          Row {row.rowNumber}: {row.name || "(no name)"} ({row.phone || "no phone"})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkAddParty;